import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { 
  Clock, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Play,
  Pause,
  CheckCircle,
  AlertTriangle,
  X,
  Download,
  Upload,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  TestTube,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { useCategories } from '../../hooks/useCategories';
import { usePriorities } from '../../hooks/usePriorities';
import { slaPolicyService } from '../../api/slaPolicyService';
import { extractErrorMessage } from '../../utils/errorHandler';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { Button } from '../../components/ui/Button';

const SLAPolicyManagement = forwardRef((props, ref) => {
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [expandedPolicies, setExpandedPolicies] = useState(new Set());
  const { data: cachedCategories = [], isFetching: fetchingCategories } = useCategories();
  const { data: cachedPriorities = [], isFetching: fetchingPriorities } = usePriorities();
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [slaTargets, setSlaTargets] = useState([
    { id: 1, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
    { id: 2, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
    { id: 3, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
    { id: 4, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } }
  ]);

  useEffect(() => {
    loadSLAPolicies();
  }, []);

  const loadSLAPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all policies from backend using fetchAll parameter
      const response = await slaPolicyService.getSLAPolicies({ fetchAll: true });
      
      let policiesData = [];
      if (response?.data?.data?.content) {
        policiesData = response.data.data.content;
      } else if (response?.data?.data) {
        policiesData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        policiesData = response.data;
      }
      
      // Group policies by category to match frontend UI structure
      const policiesByCategory = {};
      policiesData.forEach((policy) => {
        const categoryId = policy.categoryId;
        if (!policiesByCategory[categoryId]) {
          policiesByCategory[categoryId] = {
            categoryId: categoryId,
            categoryName: policy.categoryName || `Category ${categoryId}`,
            targets: []
          };
        }
        
        // Convert backend policy to target format
        const target = {
        priorityId: policy.priorityId,
          priorityName: policy.priorityName || 'Unknown',
          responseTime: policy.responseTime, // in minutes
          resolutionTime: policy.resolutionTime, // in minutes
          isActive: policy.isActive
        };
        
        policiesByCategory[categoryId].targets.push(target);
      });
      
      // Transform to frontend format (one policy per category with multiple targets)
      const transformedPolicies = Object.values(policiesByCategory).map((catPolicy, index) => {
        const activeTargets = catPolicy.targets.filter(t => t.isActive !== false);
        const allActive = catPolicy.targets.every(t => t.isActive !== false);
        
        return {
          id: `policy-${catPolicy.categoryId}`,
          categoryId: catPolicy.categoryId,
          name: catPolicy.categoryName,
          description: `SLA Policy for ${catPolicy.categoryName}`,
          status: allActive ? 'active' : 'inactive',
          priority: 'medium', // Default for display
          slaTargets: catPolicy.targets.map(t => ({
            metric: `Priority ${t.priorityName}`,
            target: `Response: ${formatTimeFromMinutes(t.responseTime)}, Resolution: ${formatTimeFromMinutes(t.resolutionTime)}`
          })),
          escalationRules: [],
          conditions: [`Category: ${catPolicy.categoryName}`],
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          complianceRate: 95,
          // Store raw targets for editing
          rawTargets: catPolicy.targets
        };
      });
      
      // Calculate stats
      const totalPolicies = transformedPolicies.length;
      const activePolicies = transformedPolicies.filter(p => p.status === 'active').length;
      
      setSlaData({
        slaPolicyManagement: { policies: transformedPolicies },
        slaStats: {
          totalPolicies: totalPolicies,
          activePolicies: activePolicies,
          violationsToday: 0, // Can be calculated from tickets later
          complianceRate: 95 // Can be calculated from tickets later
        }
      });
      
      if (totalPolicies > 0) {
        toast.success(`Loaded ${totalPolicies} SLA policy(ies)`);
      }
    } catch (err) {
        console.error('SLA data error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load SLA policies';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert minutes to display format
  const formatTimeFromMinutes = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    const days = Math.floor(minutes / 1440);
    const remainingMinutes = minutes % 1440;
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${days}d ${hours}h ${mins}m` : `${days}d ${hours}h`;
    }
    return mins > 0 ? `${days}d ${mins}m` : `${days}d`;
  };
  
  // Helper function to convert minutes to hours:minutes format object {hours, minutes}
  const convertMinutesToDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return { hours: '', minutes: '' };
    }
    
    const totalHours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    return {
      hours: String(totalHours),
      minutes: String(mins).padStart(2, '0')
    };
  };
  
  // Helper function to convert hours:minutes format object to minutes
  const convertDurationToMinutes = (timeObj) => {
    if (!timeObj || !timeObj.hours || timeObj.hours === '') return 0;
    
    const hours = parseInt(timeObj.hours, 10) || 0;
    const minutes = parseInt(timeObj.minutes || '0', 10) || 0;
    
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes >= 60) {
      return 0;
    }
    
    return (hours * 60) + minutes;
  };
  
  // Helper function to convert minutes to HH:MM format (for backward compatibility)
  const convertMinutesToTime = (minutes) => {
    if (!minutes || minutes === 0) {
      return '';
    }
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    // Format as HH:MM (24-hour format)
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };
  
  // Helper function to convert HH:MM format to minutes (for backward compatibility)
  const convertTimeToMinutes = (timeString) => {
    if (!timeString || timeString.trim() === '') return 0;
    
    // Time input format is HH:MM
    const parts = timeString.split(':');
    if (parts.length !== 2) return 0;
    
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes >= 60) {
      return 0;
    }
    
    return (hours * 60) + minutes;
  };

  const parsePoliciesResponse = (policiesResponse) => {
    if (policiesResponse?.data?.data?.content) {
      return policiesResponse.data.data.content;
    }
    if (policiesResponse?.data?.data) {
      return Array.isArray(policiesResponse.data.data) ? policiesResponse.data.data : [];
    }
    if (Array.isArray(policiesResponse?.data)) {
      return policiesResponse.data;
    }
    return [];
  };

  /** Create flow: only categories that do not already have an SLA policy */
  const loadCategoriesWithoutSla = async () => {
    try {
      setLoadingCategories(true);
      const policiesResponse = await slaPolicyService.getSLAPolicies({ fetchAll: true });
      const categoryIdsWithSla = new Set();
      parsePoliciesResponse(policiesResponse).forEach((policy) => {
        if (policy?.categoryId != null && policy.categoryId !== '') {
          categoryIdsWithSla.add(String(policy.categoryId));
        }
      });

      const available = cachedCategories.filter((category) => {
        if (category.isActive === false) return false;
        const id = String(category.categoryId ?? category.id ?? '');
        return id && id !== 'null' && !categoryIdsWithSla.has(id);
      });

      setCategories(available);
    } catch (error) {
      console.error('Error loading categories without SLA:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Populate form dropdowns from session-cached master data when the policy form opens
  useEffect(() => {
    if (!showPolicyForm) {
      return undefined;
    }

    setLoadingPriorities(fetchingPriorities);
    setPriorities(
      cachedPriorities.filter((p) => p.priorityId !== null && p.priorityId !== undefined)
    );

    const syncCategories = async () => {
      if (selectedPolicy) {
        setLoadingCategories(fetchingCategories);
        setCategories(cachedCategories);
        return;
      }
      await loadCategoriesWithoutSla();
    };

    syncCategories();
    return undefined;
  }, [showPolicyForm, selectedPolicy, cachedCategories, cachedPriorities, fetchingCategories, fetchingPriorities]);

  const handleCreatePolicy = () => {
    setSelectedPolicy(null);
    setSelectedCategoryId('');
    setSelectedStatus('active');
    // Reset SLA targets to default when creating new
    setSlaTargets([
      { id: 1, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 2, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 3, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 4, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } }
    ]);
    setShowPolicyForm(true);
  };

  // Expose handleCreatePolicy to parent component via ref
  useImperativeHandle(ref, () => ({
    openCreateForm: handleCreatePolicy
  }));

  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    setSelectedCategoryId(policy.categoryId?.toString() || '');
    setSelectedStatus(policy.status === 'active' ? 'active' : 'inactive');
    
    // Populate targets from policy's rawTargets if available
    if (policy.rawTargets && policy.rawTargets.length > 0) {
      const targets = policy.rawTargets.map((target, index) => {
        // Convert minutes back to hours:minutes format for duration input
        const resolutionTime = convertMinutesToDuration(target.resolutionTime);
        // TEMPORARY: Response Time UI hidden — keep internal responseTime synced with resolutionTime
        // const responseTime = convertMinutesToDuration(target.responseTime);
        
        return {
          id: index + 1,
          priorityId: target.priorityId?.toString() || '',
          priority: target.priorityName || '',
          responseTime: { ...resolutionTime },
          resolutionTime: resolutionTime
        };
      });
      setSlaTargets(targets);
    } else {
      // Reset to default if no targets
      setSlaTargets([
        { id: 1, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
        { id: 2, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
        { id: 3, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
        { id: 4, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } }
      ]);
    }
    
    setShowPolicyForm(true);
  };

  const handleClosePolicyForm = () => {
    setShowPolicyForm(false);
    setSelectedPolicy(null);
    setSelectedCategoryId('');
    setSelectedStatus('active');
    // Reset SLA targets to default when closing
    setSlaTargets([
      { id: 1, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 2, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 3, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } },
      { id: 4, priorityId: '', priority: '', responseTime: { hours: '', minutes: '' }, resolutionTime: { hours: '', minutes: '' } }
    ]);
  };

  const handleAddTarget = () => {
    const newId = Math.max(...slaTargets.map(t => t.id), 0) + 1;
    setSlaTargets([...slaTargets, { 
      id: newId, 
      priorityId: '', 
      priority: '', 
      responseTime: { hours: '', minutes: '' }, 
      resolutionTime: { hours: '', minutes: '' } 
    }]);
  };

  const handleRemoveTarget = (id) => {
    setSlaTargets(slaTargets.filter(target => target.id !== id));
  };

  const handleTargetChange = (id, field, value) => {
    setSlaTargets(slaTargets.map(target => {
      if (target.id === id) {
        const updated = { ...target, [field]: value };
        return updated;
      }
      return target;
    }));
  };
  
  const handleTimeChange = (targetId, timeType, field, value) => {
    setSlaTargets(slaTargets.map(target => {
      if (target.id !== targetId) return target;
      // TEMPORARY: Response Time UI hidden — keep responseTime synced with resolutionTime
      if (timeType === 'resolutionTime') {
        const next = {
          ...target.resolutionTime,
          [field]: value
        };
        return {
          ...target,
          resolutionTime: next,
          responseTime: { ...next }
        };
      }
      const updated = {
        ...target,
        [timeType]: {
          ...target[timeType],
          [field]: value
        }
      };
      return updated;
    }));
  };
  
  const handlePriorityChange = (targetId, priorityId) => {
    const selectedPriority = priorities.find(p => p.priorityId?.toString() === priorityId?.toString());
    if (selectedPriority) {
      setSlaTargets(slaTargets.map(target => 
        target.id === targetId 
          ? { 
              ...target, 
              priorityId: selectedPriority.priorityId?.toString() || '',
              priority: selectedPriority.priorityName || selectedPriority.priorityCode || ''
            } 
          : target
      ));
    }
  };

  const handleSubmitPolicy = async () => {
    // Validate form
    if (!selectedCategoryId) {
      toast.error('Please select a category');
      return;
    }
    
    // Validate that at least one target has priority and resolution time
    // TEMPORARY: Response Time validation commented out — responseTime is mirrored from resolutionTime
    const validTargets = slaTargets.filter(target => 
      target.priorityId && 
      // target.responseTime?.hours && 
      // target.responseTime?.minutes !== undefined &&
      target.resolutionTime?.hours !== undefined &&
      target.resolutionTime?.hours !== '' &&
      target.resolutionTime?.minutes !== undefined &&
      target.resolutionTime?.minutes !== ''
    );
    
    if (validTargets.length === 0) {
      toast.error('Please add at least one SLA target with priority and resolution time');
      return;
    }
    
    setSaving(true);
    try {
      const isActive = selectedStatus === 'active';
      let successCount = 0;
      let errorCount = 0;
      
      // Backend will check if category exists and update or create accordingly
      // Create/Update one policy per target (backend structure: one policy = one category + one priority)
      for (const target of validTargets) {
        try {
          const resolutionTimeMinutes = convertDurationToMinutes(target.resolutionTime);
          // TEMPORARY: Response Time unused in UI — always send responseTime = resolutionTime
          const responseTimeMinutes = resolutionTimeMinutes;
          
          if (resolutionTimeMinutes <= 0) {
            const priorityName = priorities.find(p => p.priorityId?.toString() === target.priorityId?.toString())?.priorityName || target.priority || 'Unknown';
            toast.error(`Invalid time for priority ${priorityName}. Please enter a valid resolution time.`);
            errorCount++;
            continue;
          }
          
          // TEMPORARY: response vs resolution comparison unused while both are identical
          // if (resolutionTimeMinutes < responseTimeMinutes) {
          //   const priorityName = priorities.find(p => p.priorityId?.toString() === target.priorityId?.toString())?.priorityName || target.priority || 'Unknown';
          //   toast.error(`Resolution time must be greater than or equal to response time for priority ${priorityName}`);
          //   errorCount++;
          //   continue;
          // }
          
          const policyData = {
            categoryId: parseInt(selectedCategoryId),
            priorityId: parseInt(target.priorityId),
            responseTime: responseTimeMinutes,
            resolutionTime: resolutionTimeMinutes,
            organizationId: null, // Optional - can be added later
            isActive: isActive
          };
          
        await slaPolicyService.createSLAPolicy(policyData);
          successCount++;
        } catch (error) {
          console.error(`Error creating policy for priority ${target.priority}:`, error);
          const priorityName = priorities.find(p => p.priorityId?.toString() === target.priorityId?.toString())?.priorityName || target.priority || 'Unknown';
          const errorMessage = extractErrorMessage(error, 'Failed to create policy');
          toast.error(`Failed to create policy for ${priorityName}: ${errorMessage}`);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully ${selectedPolicy ? 'updated' : 'created'} ${successCount} SLA policy(ies)`);
      await loadSLAPolicies();
        handleClosePolicyForm();
      } else if (errorCount > 0) {
        toast.error('Failed to save SLA policies. Please check the errors above.');
      }
    } catch (error) {
      console.error('Error submitting SLA policies:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to submit SLA policies');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = (policy) => {
    setPolicyToDelete(policy);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePolicy = async () => {
    if (!policyToDelete) return;

    setSaving(true);
    try {
      // Delete all policies for this category (since backend has one policy per category+priority)
      // We need to fetch all policies for this category and delete them
      const response = await slaPolicyService.getSLAPolicies({ 
        categoryId: policyToDelete.categoryId,
        fetchAll: true
      });
      
      let policiesToDelete = [];
      if (response?.data?.data?.content) {
        policiesToDelete = response.data.data.content;
      } else if (response?.data?.data) {
        policiesToDelete = Array.isArray(response.data.data) ? response.data.data : [];
      }
      
      let deletedCount = 0;
      for (const policy of policiesToDelete) {
        try {
          await slaPolicyService.deleteSLAPolicy(policy.slaPolicyId);
          deletedCount++;
        } catch (error) {
          console.error(`Error deleting policy ${policy.slaPolicyId}:`, error);
        }
      }
      
      if (deletedCount > 0) {
        toast.success(`Deleted ${deletedCount} SLA policy(ies)`);
      await loadSLAPolicies();
      } else {
        toast.error('Failed to delete SLA policies');
      }
      
      setShowDeleteConfirm(false);
      setPolicyToDelete(null);
    } catch (error) {
      console.error('Error deleting SLA policies:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to delete SLA policies');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePolicyStatus = async (policy, currentStatus) => {
    const newStatus = currentStatus === 'active' ? false : true;
    
    try {
      // Update all policies for this category
      const response = await slaPolicyService.getSLAPolicies({ 
        categoryId: policy.categoryId,
        fetchAll: true
      });
      
      let policiesToUpdate = [];
      if (response?.data?.data?.content) {
        policiesToUpdate = response.data.data.content;
      } else if (response?.data?.data) {
        policiesToUpdate = Array.isArray(response.data.data) ? response.data.data : [];
      }
      
      let updatedCount = 0;
      for (const policyToUpdate of policiesToUpdate) {
        try {
          await slaPolicyService.updateSLAPolicy(policyToUpdate.slaPolicyId, {
            categoryId: policyToUpdate.categoryId,
            priorityId: policyToUpdate.priorityId,
            responseTime: policyToUpdate.responseTime,
            resolutionTime: policyToUpdate.resolutionTime,
            organizationId: policyToUpdate.organizationId,
            isActive: newStatus
          });
          updatedCount++;
        } catch (error) {
          console.error(`Error updating policy ${policyToUpdate.slaPolicyId}:`, error);
        }
      }
      
      if (updatedCount > 0) {
      toast.success(`Policy ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await loadSLAPolicies();
      } else {
        toast.error('Failed to update policy status');
      }
    } catch (error) {
      console.error('Error toggling policy status:', error);
      toast.error('Failed to update policy status');
    }
  };

  const handleTestPolicy = (policyId) => {
    //console.log('Testing policy:', policyId);
    // In a real app, this would call an API to test the policy
  };

  const togglePolicyExpansion = (policyId) => {
    const newExpanded = new Set(expandedPolicies);
    if (newExpanded.has(policyId)) {
      newExpanded.delete(policyId);
    } else {
      newExpanded.add(policyId);
    }
    setExpandedPolicies(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading SLA policy management...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!slaData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No SLA data available</p>
        </div>
      </div>
    );
  }

  const { 
    slaPolicyManagement, 
    slaStats
  } = slaData;

  const { policies } = slaPolicyManagement;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'inactive': return 'text-gray-600 dark:text-gray-400';
      case 'draft': return 'text-yellow-600 dark:text-yellow-400';
      case 'expired': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return X;
      case 'draft': return Clock;
      case 'expired': return AlertTriangle;
      default: return Clock;
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return AlertCircle;
      case 'high': return AlertTriangle;
      case 'medium': return Clock;
      case 'low': return CheckCircle;
      default: return Clock;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 dark:text-red-400';
      case 'high': return 'text-orange-600 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    return `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`;
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         policy.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || policy.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || policy.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      {/* <div className="py-8"> */}
      <div >
        {/* SLA Stats */}
        {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Policies</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{slaStats.totalPolicies}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Policies</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{slaStats.activePolicies}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Violations Today</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{slaStats.violationsToday}</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Compliance Rate</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{slaStats.complianceRate}%</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Policies Content */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">SLA Policies</h2>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        onClick={loadSLAPolicies}
                        disabled={loading}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                      </Button>
                      <Button
                        onClick={handleCreatePolicy}
                        className="flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Create Policies</span>
                      </Button>
                    </div>
                  </div>

                  {/* Search and Filters */}
                  <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search policies..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        {/* <option value="draft">Draft</option>
                        <option value="expired">Expired</option> */}
                      </select>
                      {/* <select
                        value={filterPriority}
                        onChange={(e) => setFilterPriority(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="all">All Priorities</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select> */}
                    </div>
                  </div>

                  {/* Policies List */}
                    <div className="space-y-3">
                      {filteredPolicies.map((policy) => {
                        const StatusIcon = getStatusIcon(policy.status);
                      const PriorityIcon = getPriorityIcon(policy.priority);
                      const isExpanded = expandedPolicies.has(policy.id);
                        
                        return (
                        <div key={policy.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="flex items-center justify-between p-4">
                              <div className="flex items-center space-x-4">
                                <button
                                onClick={() => togglePolicyExpansion(policy.id)}
                                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                  )}
                                </button>
                                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                  <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{policy.name}</h3>
                                  <div className="flex items-center space-x-1">
                                    <PriorityIcon className={`h-3 w-3 ${getPriorityColor(policy.priority)}`} />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{policy.priority}</span>
                                  </div>
                                  </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{policy.description}</p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <StatusIcon className={`h-4 w-4 ${getStatusColor(policy.status)}`} />
                                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                  policy.status === 'active' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                policy.status === 'inactive' ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                                policy.status === 'draft' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                }`}>
                                  {policy.status}
                                </span>
                                <div className="flex items-center space-x-1">
                                {/* <button
                                  onClick={() => handleTestPolicy(policy.id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  title="Test Policy"
                                >
                                  <TestTube className="h-4 w-4 text-gray-400" />
                                </button> */}
                                  <button
                                    onClick={() => handleEditPolicy(policy)}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                  >
                                    <Edit3 className="h-4 w-4 text-gray-400" />
                                  </button>
                                  <button
                                  onClick={() => handleTogglePolicyStatus(policy, policy.status)}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title={policy.status === 'active' ? 'Deactivate' : 'Activate'}
                                  >
                                    {policy.status === 'active' ? (
                                      <Pause className="h-4 w-4 text-gray-400" />
                                    ) : (
                                      <Play className="h-4 w-4 text-gray-400" />
                                    )}
                                  </button>
                                  <button
                                  onClick={() => handleDeletePolicy(policy)}
                                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    title="Delete Policy"
                                  >
                                    <Trash2 className="h-4 w-4 text-gray-400" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Expanded Policy Details */}
                            {isExpanded && (
                              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">SLA Targets</h4>
                                    <div className="space-y-2">
                                    {policy.slaTargets.map((target, index) => (
                                      <div key={index} className="flex items-center justify-between text-sm">
                                        <span className="text-gray-700 dark:text-gray-300">{target.metric}</span>
                                        <span className="font-medium text-gray-900 dark:text-gray-100">{target.target}</span>
                                      </div>
                                    ))}
                                    </div>
                                  </div>
                                  <div>
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Escalation Rules</h4>
                                  <div className="space-y-2">
                                    {policy.escalationRules.map((rule, index) => (
                                      <div key={index} className="flex items-center space-x-2 text-sm">
                                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                                        <span className="text-gray-700 dark:text-gray-300">{rule}</span>
                                      </div>
                                    ))}
                                      </div>
                                        </div>
                                    </div>
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Conditions</h4>
                                <div className="space-y-2">
                                  {policy.conditions.map((condition, index) => (
                                    <div key={index} className="flex items-center space-x-2 text-sm">
                                      <CheckCircle className="h-4 w-4 text-purple-500" />
                                      <span className="text-gray-700 dark:text-gray-300">{condition}</span>
                                  </div>
                                  ))}
                                </div>
                              </div>
                                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>Created: {new Date(policy.createdDate).toLocaleDateString()}</span>
                                <span>Last Modified: {new Date(policy.lastModified).toLocaleDateString()}</span>
                                <span>Compliance: {policy.complianceRate}%</span>
                                  </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                </div>
                  </div>
                          </div>

      {/* Policy Form Modal */}
      {showPolicyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {selectedPolicy ? 'Edit SLA Policy' : 'Create New SLA Policy'}
              </h3>
              <button
                onClick={handleClosePolicyForm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-6">
              <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Policy Name
                  </label>
                  <input
                    type="text"
                    defaultValue={selectedPolicy?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Enter policy name"
                  />
                 </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select 
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={loadingCategories || saving || !!selectedPolicy}
                  >
                    <option value="">
                      {loadingCategories
                        ? 'Loading categories...'
                        : !selectedPolicy && categories.length === 0
                          ? 'No categories available (all have SLA policies)'
                          : 'Select a category...'}
                    </option>
                    {categories.map((category) => (
                      <option key={category.categoryId || category.id} value={category.categoryId || category.id}>
                        {category.categoryName || category.name}
                      </option>
                    ))}
                  </select>
                  {!selectedPolicy && !loadingCategories && categories.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                      Every category already has an SLA policy. Delete an existing policy to create one for that category.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                  <select 
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    disabled={saving}
                  >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  </select>
              </div>

              {/* SLA Targets Section */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  SLA Targets <span className="text-red-500">*</span>
                </label>
                <div className="space-y-4">
                  {slaTargets.map((target) => (
                    <div key={target.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Priority
                  </label>
                        <select
                          value={target.priorityId || ''}
                          onChange={(e) => handlePriorityChange(target.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          disabled={loadingPriorities || saving}
                        >
                          <option value="">Select priority...</option>
                          {priorities
                            .filter((priority) => {
                              const name = String(priority.priorityName || '').toLowerCase().replace(/[-_]/g, ' ').trim();
                              const code = String(priority.priorityCode || '').toLowerCase().replace(/[-_]/g, ' ').trim();
                              return name !== 'not prioritised' && name !== 'not prioritized'
                                && code !== 'not prioritised' && code !== 'not prioritized';
                            })
                            .map((priority) => (
                            <option key={priority.priorityId} value={priority.priorityId}>
                              {priority.priorityName || priority.priorityCode || `Priority ${priority.priorityId}`}
                            </option>
                          ))}
                        </select>
                </div>
                {/* TEMPORARY: Response Time UI hidden — restore by uncommenting this block
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Response Time
                  </label>
                  <div className="flex gap-2 items-center">
                  <input
                    type="number"
                            min="0"
                            value={target.responseTime?.hours || ''}
                            onChange={(e) => handleTimeChange(target.id, 'responseTime', 'hours', e.target.value)}
                            className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center"
                            placeholder="Hours"
                            disabled={saving}
                    />
                    <span className="text-gray-600 dark:text-gray-400">:</span>
                    <input
                            type="number"
                            min="0"
                            max="59"
                            value={target.responseTime?.minutes || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const padded = val === '' ? '' : String(parseInt(val, 10) || 0).padStart(2, '0');
                              handleTimeChange(target.id, 'responseTime', 'minutes', padded);
                            }}
                            className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center"
                            placeholder="MM"
                            disabled={saving}
                  />
                </div>
              </div>
                */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Resolution Time
                </label>
                  <div className="flex gap-2 items-center">
                    <input
                    type="number"
                    min="0"
                            value={target.resolutionTime?.hours || ''}
                            onChange={(e) => handleTimeChange(target.id, 'resolutionTime', 'hours', e.target.value)}
                            className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center"
                            placeholder="Hours"
                    disabled={saving}
                  />
                    <span className="text-gray-600 dark:text-gray-400">:</span>
                    <input
                    type="number"
                    min="0"
                            max="59"
                            value={target.resolutionTime?.minutes || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const padded = val === '' ? '' : String(parseInt(val, 10) || 0).padStart(2, '0');
                              handleTimeChange(target.id, 'resolutionTime', 'minutes', padded);
                            }}
                            className="w-20 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center"
                            placeholder="MM"
                    disabled={saving}
                  />
                </div>
              </div>
                      <div className="flex items-end">
                        <button
                type="button"
                          onClick={() => handleRemoveTarget(target.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          title="Remove target"
                disabled={saving}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
            </div>
    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddTarget}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  disabled={saving}
                  >
                    <Plus className="h-4 w-4" />
                    Add Target
                  </button>
              </div>
            </div>
            </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                onClick={handleClosePolicyForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitPolicy}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={saving}
              >
                {saving ? (selectedPolicy ? 'Updating...' : 'Creating...') : (selectedPolicy ? 'Update Policy' : 'Create Policy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPolicyToDelete(null);
        }}
        onConfirm={confirmDeletePolicy}
        title="Delete SLA Policy"
        message={
          policyToDelete 
            ? `Are you sure you want to delete the SLA policy for "${policyToDelete.name}"? This will delete all priority targets for this category. This action cannot be undone.`
            : 'Are you sure you want to delete this SLA policy?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
});

SLAPolicyManagement.displayName = 'SLAPolicyManagement';

export default SLAPolicyManagement;
